import { useCallback, useEffect, useState } from "react";
import { Bell, Check, X, Users, ArrowClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";

const STATUS_STYLE = {
  in_coda: "bg-amber-100 text-amber-800 border-amber-300",
  chiamato: "bg-emerald-100 text-emerald-800 border-emerald-300",
  seduto: "bg-sky-100 text-sky-800 border-sky-300",
  no_show: "bg-rose-100 text-rose-700 border-rose-300",
  cancellato: "bg-zinc-100 text-zinc-500 border-zinc-300",
};
const STATUS_LABEL = { in_coda: "IN CODA", chiamato: "CHIAMATO", seduto: "SEDUTO",
  no_show: "NO-SHOW", cancellato: "CANCELLATO" };

export default function Coda() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");
  const [showClosed, setShowClosed] = useState(false);

  const load = useCallback((silent = false) => {
    api.get("/queue/today").then((r) => setData(r.data))
      .catch(() => { if (!silent) toast.error("Errore caricamento coda"); });
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(() => load(true), 20000);
    return () => clearInterval(t);
  }, [load]);

  const setStatus = async (entry, status) => {
    setBusy(entry.id + status);
    try {
      const r = await api.post(`/queue/${entry.id}/status`, { status });
      if (status === "chiamato") {
        toast.success(r.data.notified === "inviato"
          ? `${entry.name} chiamato — avviso WhatsApp inviato`
          : `${entry.name} chiamato (avviso WhatsApp non inviato)`);
      } else {
        toast.success(`Stato aggiornato: ${STATUS_LABEL[status]}`);
      }
      load(true);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };

  const entries = data?.entries || [];
  const open = entries.filter((e) => ["in_coda", "chiamato"].includes(e.status));
  const closed = entries.filter((e) => !["in_coda", "chiamato"].includes(e.status));
  const byForm = open.reduce((acc, e) => {
    (acc[e.web_form_name || "Coda"] = acc[e.web_form_name || "Coda"] || []).push(e);
    return acc;
  }, {});

  return (
    <div className="space-y-4" data-testid="coda-page">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-zinc-900">Coda walk-in</h1>
          <p className="text-xs text-zinc-500">Clienti in attesa senza prenotazione · {data?.date || "oggi"} · aggiornamento automatico ogni 20s</p>
        </div>
        <button onClick={() => load()} data-testid="coda-refresh"
          className="h-10 px-4 rounded-md bg-white border border-zinc-300 text-xs font-semibold text-zinc-700 flex items-center gap-1.5">
          <ArrowClockwise size={14} /> Aggiorna
        </button>
      </div>

      {!data && <div className="text-sm text-zinc-400 py-8 text-center">Caricamento...</div>}
      {data && open.length === 0 && (
        <div className="bg-white border border-zinc-200 rounded-lg py-12 text-center" data-testid="coda-empty">
          <Users size={28} className="mx-auto text-zinc-300 mb-2" />
          <p className="text-sm text-zinc-400">Nessun cliente in coda al momento.</p>
        </div>
      )}

      {Object.entries(byForm).map(([formName, list]) => (
        <div key={formName} className="space-y-2">
          <h2 className="text-xs font-bold uppercase tracking-wider text-zinc-500">{formName}</h2>
          {list.map((e) => (
            <div key={e.id} className="bg-white border border-zinc-200 rounded-lg p-4"
              data-testid={`coda-entry-${e.id}`}>
              <div className="flex items-center gap-2 flex-wrap">
                {e.status === "in_coda" && (
                  <span className="w-8 h-8 rounded-full bg-zinc-900 text-white text-sm font-bold flex items-center justify-center"
                    data-testid={`coda-position-${e.id}`}>{e.position}</span>
                )}
                <span className="font-display font-bold text-zinc-900">{e.name}</span>
                <span className="text-xs text-zinc-500">{e.people} {e.people === 1 ? "persona" : "persone"} · {e.phone}</span>
                <span className={`ml-auto text-[10px] font-bold px-2 py-0.5 rounded-full border ${STATUS_STYLE[e.status]}`}
                  data-testid={`coda-status-${e.id}`}>{STATUS_LABEL[e.status]}</span>
              </div>
              {e.notes && <p className="text-xs text-zinc-500 mt-1.5">Note: {e.notes}</p>}
              <div className="flex gap-1.5 mt-3 flex-wrap">
                {e.status === "in_coda" && (
                  <button onClick={() => setStatus(e, "chiamato")} disabled={!!busy}
                    data-testid={`coda-chiama-${e.id}`}
                    className="h-9 px-4 rounded-md bg-emerald-600 text-white text-xs font-bold flex items-center gap-1.5 disabled:opacity-40">
                    <Bell size={14} weight="bold" /> Chiama
                  </button>
                )}
                {e.status === "chiamato" && (
                  <button onClick={() => setStatus(e, "seduto")} disabled={!!busy}
                    data-testid={`coda-seduto-${e.id}`}
                    className="h-9 px-4 rounded-md bg-sky-600 text-white text-xs font-bold flex items-center gap-1.5 disabled:opacity-40">
                    <Check size={14} weight="bold" /> Segna seduto
                  </button>
                )}
                <button onClick={() => setStatus(e, "no_show")} disabled={!!busy}
                  data-testid={`coda-noshow-${e.id}`}
                  className="h-9 px-3 rounded-md bg-white border border-rose-200 text-rose-600 text-xs font-semibold disabled:opacity-40">
                  No-show
                </button>
                {e.status === "in_coda" && (
                  <button onClick={() => setStatus(e, "cancellato")} disabled={!!busy}
                    data-testid={`coda-cancella-${e.id}`}
                    className="h-9 px-3 rounded-md bg-white border border-zinc-300 text-zinc-500 text-xs font-semibold disabled:opacity-40">
                    <X size={13} /> Rimuovi
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      ))}

      {closed.length > 0 && (
        <div>
          <button onClick={() => setShowClosed(!showClosed)} data-testid="coda-toggle-chiusi"
            className="text-xs font-semibold text-zinc-500 underline">
            {showClosed ? "Nascondi" : "Mostra"} chiusi di oggi ({closed.length})
          </button>
          {showClosed && (
            <div className="mt-2 space-y-1" data-testid="coda-chiusi">
              {closed.map((e) => (
                <div key={e.id} className="bg-zinc-50 border border-zinc-200 rounded-lg px-3 py-2 flex items-center gap-2 text-xs opacity-70">
                  <span className="font-semibold text-zinc-700">{e.name}</span>
                  <span className="text-zinc-500">{e.people} pers.</span>
                  <span className={`ml-auto text-[10px] font-bold px-2 py-0.5 rounded-full border ${STATUS_STYLE[e.status]}`}>
                    {STATUS_LABEL[e.status]}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
